import React from 'react';
import { Configuration, Widget, WidgetTextarea } from 'jamespot-user-api';
import * as ListFormatter from './formatter';
import {
    Displayer,
    DisplayerElement,
    Field,
    FieldWith,
    Fields,
    FieldsWith,
    ensureConfigurableField,
    DisplayFormFieldsConfig,
} from './types';
import type { FieldValues, FieldPath } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { store } from '../redux/initStore';
import { useAppSelector } from '../redux/store';

function formatField<
    TFieldValues extends FieldValues = FieldValues,
    Properties extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(configuration: Configuration<Properties>): DisplayerElement<TFieldValues> {
    switch (configuration.widget.type) {
        case 'audience':
            return ListFormatter.formatAudience(configuration);
        case 'checkbox':
            return ListFormatter.formatCheckbox(configuration);
        case 'date':
        case 'datetime':
            return ListFormatter.formatDate(configuration);
        case 'email':
            return ListFormatter.formatEmail(configuration);
        case 'file':
            return ListFormatter.formatFile(configuration);
        case 'image':
            return ListFormatter.formatImage(configuration);
        case 'number':
            return ListFormatter.formatNumber(configuration);
        case 'orientedlinks':
            return ListFormatter.formatOrientedlinks(configuration);
        case 'radio':
            return ListFormatter.formatRadio(configuration);
        case 'select':
            return ListFormatter.formatSelect(configuration);
        case 'taxonomy':
            return ListFormatter.formatTaxonomy<TFieldValues, Properties>(configuration);
        case 'textarea':
            if ((configuration.widget as WidgetTextarea).params?.class === 'mceEditor') {
                return ListFormatter.formatRichText(configuration);
            }
            return ListFormatter.formatTextarea(configuration);
        case 'toggle':
            return ListFormatter.formatToggle(configuration);
        case 'uri':
            return ListFormatter.formatUri(configuration);
        case 'url':
            return ListFormatter.formatUrl(configuration);
        case 'text':
        default:
            return ListFormatter.formatDefault(configuration);
    }
}

function normalizeField<With extends 'input' | 'render', TFieldValues extends FieldValues = FieldValues>(
    field: FieldWith<With, TFieldValues>,
    renderAttribute: With,
): Field<TFieldValues> {
    if (typeof field === 'string') {
        return field;
    } else if ('includes' in field) {
        return field;
    } else if ('extra' in field) {
        return field;
    } else if (ensureConfigurableField(field)) {
        return field;
    } else {
        return {
            name: field.name,
            componentsOverride: {
                [renderAttribute]: field[renderAttribute],
            },
            config: field.config,
        };
    }
}

function getDefaultValue<Properties extends string = string>(configuration: Configuration<Properties>) {
    switch (configuration.widget.type) {
        case 'checkbox':
        case 'taxonomy':
        case 'audience':
            return [configuration.name, []];
        case 'select':
            const value = configuration.widget.params?.defaultValue;
            if (value) {
                const label = configuration.widget.options[value];
                if (label) {
                    return [configuration.name, { label, value }];
                }
            }
            return [configuration.name, ''];
        default:
            return [configuration.name, ''];
    }
}

/**
 *
 * @param fields - fields that we want to display (firstname, name, age...)
 * @param type - model type (user, article, spot...)
 * @param bypassRequiredFields
 */
export function useDisplay<
    TFieldValues extends FieldValues = FieldValues,
    Properties extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(fields: Fields<TFieldValues> | 'all', type: string, bypassRequiredFields?: boolean): Displayer<TFieldValues> {
    const intl = useIntl();
    const model = useAppSelector((state) => store.selectors.model.selectById(state, type));

    if (!model || !model.fields) return [];

    const configuration = Object.values(model.fields);

    const ensureConfigurationProperties = (
        configuration: Configuration<string>,
    ): configuration is Configuration<Properties> => {
        return typeof configuration === 'object' && 'name' in configuration;
    };

    const configurationProperties = configuration
        .map((c) => {
            return ensureConfigurationProperties(c) ? [c] : [];
        })
        .flat();

    const toRet: Displayer<TFieldValues> = [];

    if (fields === 'all') {
        configurationProperties.forEach((c) => {
            const formattedField = formatField<TFieldValues, Properties>(c);
            toRet.push({
                ...formattedField,
                components: { ...formattedField.components },
            });
        });
    } else {
        for (const field of fields) {
            if (typeof field === 'object' && 'includes' in field) {
                if (field.includes === 'tags') {
                    const tags = configurationProperties.filter(
                        (conf): conf is Configuration<Properties> => conf.widget.type === 'taxonomy',
                    );
                    toRet.push(...tags.map((tag) => formatField<TFieldValues, Properties>(tag)));
                } else if (field.includes === '_attachedFiles') {
                    toRet.push(
                        formatField<TFieldValues, Properties>({
                            name: '_attachedFiles' as Properties,
                            mandatory: false,
                            label: intl.formatMessage({ id: 'GLOBAL_Attached_Files' }),
                            widget: {
                                type: 'file',
                            },
                        }),
                    );
                }
                continue;
            }

            if (field === 'publishTo' || (ensureConfigurableField(field) && field.name === 'publishTo')) {
                toRet.push(
                    formatField<TFieldValues, Properties>({
                        name: 'publishTo' as Properties,
                        mandatory: false,
                        label: intl.formatMessage({ id: 'GLOBAL_Audience' }),
                        widget: {
                            type: 'audience',
                            options: {
                                disableEveryoneAudience: ensureConfigurableField(field)
                                    ? field.options.disableEveryoneAudience
                                    : false,
                                views: ensureConfigurableField(field) ? field.options.views : undefined,
                            },
                        },
                    }),
                );
                continue;
            }
            if (field === 'sendAlert' || field === 'alertAuthor') {
                const labelId = field === 'sendAlert' ? 'SPOTLIGHT_Send_Alert_To_Subscribers' : 'SPOTIT_Receive_A_Copy';
                toRet.push(
                    formatField<TFieldValues, Properties>({
                        name: field as Properties,
                        mandatory: false,
                        label: intl.formatMessage({ id: labelId }),
                        widget: {
                            type: 'toggle',
                        },
                    }),
                );
                continue;
            }

            if (typeof field === 'object' && 'extra' in field) {
                toRet.push(
                    formatField<TFieldValues, Properties>({
                        name: field.name as Properties,
                        mandatory: field.extra.mandatory,
                        label: field.extra.label,
                        widget: field.extra.widget,
                        description: field.extra.description,
                    }),
                );
                continue;
            }

            const fieldConfiguration = configurationProperties.find((conf): conf is Configuration<Properties> =>
                typeof field === 'string' ? conf.name === field : conf.name === field.name,
            );

            if (!fieldConfiguration) continue;

            let formattedField: DisplayerElement<TFieldValues> | undefined;
            if (fieldConfiguration) {
                formattedField = formatField<TFieldValues, Properties>(fieldConfiguration);
            }

            if (formattedField) {
                if (typeof field === 'string') {
                    toRet.push(formattedField);
                } else {
                    toRet.push({
                        ...formattedField,
                        components: { ...formattedField.components, ...field.componentsOverride },
                    });
                }
            }
        }
        if (!bypassRequiredFields) {
            configurationProperties
                .filter((c) => c.mandatory && !toRet.some((a) => a.name === c.name))
                .forEach((c) => {
                    const formattedField = formatField<TFieldValues, Properties>(c);
                    toRet.push({
                        ...formattedField,
                        components: { ...formattedField.components },
                    });
                });
        }
    }

    return toRet;
}

export function useDisplayList<TFieldValues extends FieldValues = FieldValues>(
    fields: FieldsWith<'render', TFieldValues>,
    type: string,
) {
    const configuration = useDisplay(
        fields.map((field) => normalizeField(field, 'render')),
        type,
    );

    return configuration.map(({ components, ...config }) => {
        const field = fields.find(
            (field) => typeof field === 'object' && 'name' in field && field.name === config.name,
        );
        return {
            accessor: (object: Object) => <components.render object={object} attribute={config} />,
            disableSortBy:
                ['orientedlinks', 'taxonomy'].includes(config.widget.type) ||
                (field && typeof field === 'object' && 'config' in field && field.config?.disableSortBy) ||
                undefined,
            id: config.name,
            Header: config.label,
            name: config.name,
        };
    });
}

export function useDisplayForm<TFieldValues extends FieldValues = FieldValues>(
    fields: DisplayFormFieldsConfig<TFieldValues>,
    type: string,
    forceMandatory = false,
): [
    Array<{
        Input: any;
        description?: string;
        label: string;
        name: string;
        mandatory: boolean;
        widget: Widget;
    }>,
    Record<string, '' | Array<void>>,
] {
    const configuration = useDisplay(
        fields === 'all' ? 'all' : fields.map((field) => normalizeField(field, 'input')),
        type,
        !forceMandatory,
    );

    return [
        configuration.map(({ components, description, label, name, mandatory, widget }) => ({
            Input: components.input,
            description,
            label,
            name,
            mandatory,
            widget,
        })),
        Object.fromEntries(configuration.map(getDefaultValue)),
    ];
}
